/**
 * Aetheris REST API Endpoints Configuration
 *
 * Endpoint definitions for Aetheris Backend microservices.
 * Follows microservices architecture with separate endpoints for Auth, API, and Translation services.
 *
 * @architecture Microservices-based
 * @backend Aetheris-Backend (FastAPI)
 */

import { environment } from '../../../environments/environment';

/**
 * Service base URLs from environment configuration
 */
const BASE_URLS = {
  auth: environment.api.authUrl,
  api: environment.api.apiUrl,
  translation: environment.api.translationUrl,
} as const;

/**
 * Auth Service Endpoints (Port 8000)
 * Handles authentication and user information
 */
export const AUTH_ENDPOINTS = {
  me: `${BASE_URLS.auth}/auth/me`,
  devLogin: `${BASE_URLS.auth}/auth/dev-login`,
  health: `${BASE_URLS.auth}/auth/health`,
} as const;

/**
 * Translation Service Endpoints (Port 8002)
 * Manages multi-language translations
 */
export const TRANSLATION_ENDPOINTS = {
  list: `${BASE_URLS.translation}/translations/`,
  getById: (id: number) => `${BASE_URLS.translation}/translations/${id}`,
  update: (id: number) => `${BASE_URLS.translation}/translations/${id}`,
  health: `${BASE_URLS.translation}/translations/health`,
  healthDb: `${BASE_URLS.translation}/translations/health/db`,
} as const;

/**
 * API Service - Core Endpoints (Port 8001)
 * Main business logic
 */
export const API_ENDPOINTS = {
  health: `${BASE_URLS.api}/api/health`,
  healthDb: `${BASE_URLS.api}/api/health/db`,
} as const;

/**
 * Generic CRUD endpoint builder for dynamic table access
 */
export function buildCrudEndpoints(schema: 'app' | 'sis', table: string) {
  const baseUrl = `${BASE_URLS.api}/api/${schema}/${table}`;

  return {
    list: `${baseUrl}/`,
    search: `${baseUrl}/search`,
    getById: (id: number) => `${baseUrl}/${id}`,
    create: `${baseUrl}/`,
    update: (id: number) => `${baseUrl}/${id}`,
    delete: (id: number) => `${baseUrl}/${id}`,
  };
}

/**
 * CRUD Endpoints - App Schema
 */
export const APP_SCHEMA_ENDPOINTS = {
  languages: buildCrudEndpoints('app', 'languages'),
  countries: buildCrudEndpoints('app', 'countries'),
  categoryDefinitions: buildCrudEndpoints('app', 'category_definitions'),
  contacts: {
    ...buildCrudEndpoints('app', 'contacts'),
    addresses: (contactId: number) => `${BASE_URLS.api}/api/app/contacts/${contactId}/addresses`,
    portalRole: (contactId: number) => `${BASE_URLS.api}/api/app/contacts/${contactId}/portalRole`,
  },
  categories: {
    base: `${BASE_URLS.api}/api/app/categories`,
    byComponent: (component: string) =>
      `${BASE_URLS.api}/api/app/categories/by-component/${component}`,
    sideAddressCategories: `${BASE_URLS.api}/api/app/categories/side-address-categories`,
  },
  addresses: buildCrudEndpoints('app', 'addresses'),
  contactAddresses: buildCrudEndpoints('app', 'contact_addresses'),
  contactMailPhone: buildCrudEndpoints('app', 'contact_mailphone'),
  contactCategories: buildCrudEndpoints('app', 'contact_categories'),
  modules: buildCrudEndpoints('app', 'modules'),
  roles: buildCrudEndpoints('app', 'roles'),
  functions: buildCrudEndpoints('app', 'functions'),
  roleFunctions: buildCrudEndpoints('app', 'role_functions'),
  users: buildCrudEndpoints('app', 'users'),
  userAuth: buildCrudEndpoints('app', 'user_auth'),
  userModules: buildCrudEndpoints('app', 'user_modules'),
  userRoles: buildCrudEndpoints('app', 'user_roles'),
  userFunctions: buildCrudEndpoints('app', 'user_functions'),
  settings: buildCrudEndpoints('app', 'settings'),
  userSettings: buildCrudEndpoints('app', 'user_settings'),
  schools: buildCrudEndpoints('app', 'schools'),
  schoolLocations: buildCrudEndpoints('app', 'school_locations'),
  calendar: {
    ...buildCrudEndpoints('app', 'calendar'),
    holidays: `${BASE_URLS.api}/api/app/calendar/holidays`,
    holidayByDate: `${BASE_URLS.api}/api/app/calendar/holidays/by-date`,
  },
} as const;

/**
 * CRUD Endpoints - SIS Schema
 */
export const SIS_SCHEMA_ENDPOINTS = {
  attributeDefinitions: {
    ...buildCrudEndpoints('sis', 'attribute_definitions'),
    hierarchy: `${BASE_URLS.api}/api/sis/attribute-definitions/hierarchy`,
    userAttributes: (contactId: number) =>
      `${BASE_URLS.api}/api/sis/attribute-definitions/user/${contactId}`,
    updateUserAttribute: (contactId: number, attributeId: number) =>
      `${BASE_URLS.api}/api/sis/attribute-definitions/user/${contactId}/attribute/${attributeId}`,
    deleteUserAttribute: (contactId: number, attributeId: number) =>
      `${BASE_URLS.api}/api/sis/attribute-definitions/user/${contactId}/attribute/${attributeId}`,
  },
  contactAttributes: buildCrudEndpoints('sis', 'contact_attributes'),
  contacts: buildCrudEndpoints('sis', 'contacts'),
  degrees: buildCrudEndpoints('sis', 'degrees'),
  modules: buildCrudEndpoints('sis', 'modules'),
  moduleVariants: buildCrudEndpoints('sis', 'module_variants'),
  planningAutomation: {
    search: `${BASE_URLS.api}/api/app/planning/automation/search`,
    lecturerBlocks: (contactId: number) => `${BASE_URLS.api}/api/app/planning/automation/lecturer-blocks/${contactId}`,
    lecturerAction: `${BASE_URLS.api}/api/app/planning/automation/lecturer-action`,
  },
} as const;

/**
 * Portal Finance Endpoints
 * Dedicated endpoints for student invoice & installment management
 */
export const PORTAL_FINANCE_ENDPOINTS = {
  invoicesSearch: `${BASE_URLS.api}/api/app/invoice/portal/invoices-search`,
  invoiceDetails: (invoiceId: number) => `${BASE_URLS.api}/api/app/invoice/portal/invoices/${invoiceId}/details`,
  invoicePdfUrl: (invoiceId: number) => `${BASE_URLS.api}/api/app/invoice/portal/invoices/${invoiceId}/pdf/url`,
  invoicePdfStream: (invoiceId: number) => `${BASE_URLS.api}/api/app/invoice/portal/invoices/${invoiceId}/pdf`,
  invoicePositions: (invoiceId: number) => `${BASE_URLS.api}/api/app/invoice/portal/invoices/${invoiceId}/positions`,
  studentInstallmentStatus: (contactId: number) => `${BASE_URLS.api}/api/app/invoice/portal/student-installment-status?fk_contact_id=${contactId}`,
  recalculateInstallments: `${BASE_URLS.api}/api/app/invoice/portal/invoices/recalculate-installments`,
} as const;

/**
 * Portal Student Timetable Endpoints
 */
export const PORTAL_STUDENT_TIMETABLE_ENDPOINTS = {
  lectures: (contactId: number) => `${BASE_URLS.api}/api/app/planning/timetable/portal/student-lectures?fk_contact_id=${contactId}`,
} as const;


/**
 * Lecturer Availability Endpoints
 * Handles lecturer-specific filtering for schools, locations, and variants
 */
export const LECTURER_AVAILABILITY_ENDPOINTS = {
  filters: (contactId: number) => `${BASE_URLS.api}/api/lecturer/availability/filters/${contactId}`,
  filtersDegreePostings: (contactId: number) =>
    `${BASE_URLS.api}/api/lecturer/availability/filters-degree-postings/${contactId}`,
  variantsByLocation: (contactId: number, locationId: number) =>
    `${BASE_URLS.api}/api/lecturer/availability/variants-by-location/${contactId}/${locationId}`,
  validate: (contactId: number) =>
    `${BASE_URLS.api}/api/lecturer/availability/validate/${contactId}`,
  savePreferences: (contactId: number) =>
    `${BASE_URLS.api}/api/lecturer/availability/preferences/${contactId}`,
  saveDaily: (contactId: number) => `${BASE_URLS.api}/api/lecturer/availability/daily/${contactId}`,
  getDaily: (contactId: number) => `${BASE_URLS.api}/api/lecturer/availability/daily/${contactId}`,
  saveDailyBatch: (contactId: number) =>
    `${BASE_URLS.api}/api/lecturer/availability/daily/batch/${contactId}`,
} as const;

/**
 * Pagination Configuration
 */
export const PAGINATION_CONFIG = {
  defaultSkip: 0,
  defaultLimit: 100,
  maxLimit: 1000,
} as const;

/**
 * Build URL with query parameters
 */
export function buildUrlWithParams(
  baseUrl: string,
  params?: Record<string, string | number | boolean | null | undefined>,
): string {
  if (!params) return baseUrl;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, value.toString());
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Build pagination query parameters
 */
export function buildPaginationParams(skip?: number, limit?: number): Record<string, number> {
  return {
    skip: Math.max(0, skip ?? PAGINATION_CONFIG.defaultSkip),
    limit: Math.min(limit ?? PAGINATION_CONFIG.defaultLimit, PAGINATION_CONFIG.maxLimit),
  };
}
